import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  linkedSignal,
  OnDestroy,
  OnInit,
  QueryList,
  runInInjectionContext,
  Signal,
  signal,
  ViewChild,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { GridStackOptions } from 'gridstack';
import { GridstackComponent, GridstackItemComponent } from 'gridstack/dist/angular';
import { cloneDeep, flatten, get, omit, pick, uniq } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  merge,
  Observable,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import {
  DateUtilitiesService,
  ESqlOperators,
  EWidget,
  FilterCardService,
  IAppliedFilters,
  IFilterIdentity,
  IFilterValuesVisibleToWidget,
  IframeDialogComponent,
  IframeDialogData,
  IHeaderAction,
  IWidgetExceptionalCaseInformation,
  PersistentEndpointParameterStore,
  TFilterSave,
  TInEffectFilter,
  TWidgetCustomParameters,
  TWidgetParameters,
  WIDGET_INPUTS,
  widgetConstants,
} from 'scw-digital-boards-widgets';
import { TReducerState } from '../../app.config';
import { EDialogWidth } from '../../core/constants/ui.constants';
import { InfiniteAutoScrollDirective } from '../../core/directives/infinite-auto-scroll.directive';
import { ArrayIncludesPipe } from '../../core/pipes/array-includes.pipe';
import { HeartbeatService } from '../../core/services/heartbeat.service';
import { EScreenLockEntityType, IResolvedScreenLock } from '../../core/services/screen-lock.interface';
import { ScreenLockService } from '../../core/services/screen-lock.service';
import { PageHeaderService } from '../../layout/page-header/page-header.service';
import {
  AddActionDialogComponent,
  IAddActionDialogConfig,
} from '../../shared/components/add-action-dialog/add-action-dialog.component';
import { IFilterIdentity as IAdvancedFilterIdentity } from '../../shared/components/advanced-filter/advanced-filter.interface';
import { AdvancedFilterService } from '../../shared/components/advanced-filter/advanced-filter.service';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { CountdownManager } from '../../shared/components/countdown/countdown-manager.class';
import { FormSubmissionsDialogComponent } from '../../shared/components/form-submissions-dialog/form-submissions-dialog.component';
import { PageViewAndEditModeSwitchManager } from '../../shared/components/page-view-and-edit-mode-switch/page-view-and-edit-mode-switch-manager.class';
import { dashboardContentActions } from '../../state/dashboard-content/dashboard-content.actions';
import { DashboardContentService } from '../../state/dashboard-content/dashboard-content.service';
import { ELoadStatus } from '../../state/state.interface';
import { ESnackbar, SnackbarService } from '../../utilities/snackbar.service';
import { IWorkspaceModules } from '../settings/dashboards/dashboards.interface';
import { DashboardsRoute } from '../settings/dashboards/dashboards.paths';
import { IFormSubmissionsDialogData } from '../settings/form-submissions/form-submissions.interface';
import { AddWidgetCatalogDialogComponent } from './add-widget-catalog-dialog/add-widget-catalog-dialog.component';
import { addWidgetConfiguration } from './dashboard-constants.class';
import {
  EDashboardLayout,
  IDashboardContentDialogData,
  IGridstackPositionSubmit,
  IWidgetData,
  IWidgetFilterSuppression,
  TGridItem,
  TWidgetSavePayload,
} from './dashboard-content.interface';
import { EditNoteDialogComponent, IEditNoteDialogData } from './edit-note-dialog/edit-note-dialog.component';
import { openDfPageOptions } from './widget-add-edit-dialog/input-components/open-df-page-configuration/open-df-page-configuration.interface';
import { WidgetAddEditDialogComponent } from './widget-add-edit-dialog/widget-add-edit-dialog.component';
import {
  IAddEditDialogInjectData,
  TSubDashboardWindowSize,
} from './widget-add-edit-dialog/widget-add-edit-dialog.interface';
import { WidgetExceptionalCaseInformationComponent } from './widget-exceptional-case-information/widget-exceptional-case-information.component';
import {
  IWidgetFilterDialogData,
  IWidgetFilterDialogOutput,
  WidgetFiltersDialogComponent,
} from './widget-filters-dialog/widget-filters-dialog.component';
import { WidgetWrapperComponent } from './widget-wrapper.component';

@Component({
  imports: [
    MatButton,
    AsyncPipe,
    TranslatePipe,
    GridstackItemComponent,
    GridstackComponent,
    InfiniteAutoScrollDirective,
    WidgetExceptionalCaseInformationComponent,
    MatIcon,
    MatProgressBar,
    WidgetWrapperComponent,
    NgIf,
    MatTooltip,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    ArrayIncludesPipe,
    NgClass,
    MatRipple,
  ],
  providers: [HeartbeatService, AdvancedFilterService],
  selector: 'app-dashboard-content',
  styleUrl: './dashboard-content.component.scss',
  templateUrl: './dashboard-content.component.html',
})
export class DashboardContentComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('gridstack', { static: false }) gridstack!: GridstackComponent;
  @ViewChildren('gridStackItem') gridStackItems!: QueryList<{ elementRef: ElementRef<HTMLElement> }>;

  private readonly advancedFilterService = inject(AdvancedFilterService);
  private readonly filterCardService = inject(FilterCardService);
  private readonly dashboardContentService = inject(DashboardContentService);
  private readonly screenLockService = inject(ScreenLockService);
  public readonly dialogData: IDashboardContentDialogData | undefined = inject(MAT_DIALOG_DATA, { optional: true });
  public readonly mobileBreakpoint = 1000;
  public readonly isMobileMode = (): boolean => {
    return window.innerWidth < this.mobileBreakpoint;
  };

  private readonly dashboard = this.dialogData?.dashboard
    ? signal(this.dialogData.dashboard)
    : this.globalStore.selectSignal((state) => state.dashboardContent.dashboardData!);

  public readonly EDashboardLayout = EDashboardLayout;
  public dashboardLayout: EDashboardLayout = this.dashboard().layoutModeId;

  public gridOptions: GridStackOptions = {
    animate: true,
    cellHeight: this.getCellHeight(),
    columnOpts: { breakpoints: [{ c: 1, w: this.mobileBreakpoint }] },
    float: true,
    marginBottom: 5,
    marginRight: 5,
  };

  public gridItems$: BehaviorSubject<TGridItem[]> = new BehaviorSubject<TGridItem[]>([]);
  public readonly ELoadStatus = ELoadStatus;
  public selectedItem: TGridItem | undefined;
  private skipGridRebuildOnce = false;
  private readonly subscriptions: Subscription[] = [];
  private readonly countdownManager!: CountdownManager;
  public readonly pageViewAndEditModeSwitchManager!: PageViewAndEditModeSwitchManager;
  public readonly isAnyFilterHovered = computed(() =>
    Boolean(this.advancedFilterService.manager.hoveredFilterIdentity()),
  );

  public readonly workspaceModulePermissions: Signal<IWorkspaceModules | undefined> = this.globalStore.selectSignal(
    (state) => state.dashboardContent.dashboardData?.workspace.modulePermissions,
  );

  public readonly isActionTrackerModuleAllowed: Signal<boolean> = computed(() => {
    return !!this.workspaceModulePermissions()?.actionTracker;
  });

  public readonly isOeeTrackerModuleAllowed: Signal<boolean> = computed(() => {
    return !!this.workspaceModulePermissions()?.oeeTracker;
  });

  public readonly isWidgetFilterButtonHidden: Signal<boolean> = computed(() => {
    return this.dashboard().isWidgetFilterButtonHidden;
  });

  public readonly authorizedDashboardIds = this.globalStore.selectSignal(
    (state) => state.user.user!.authorizations.dashboardIds,
  );

  public readonly oeeTrackerUrl = this.globalStore.selectSignal(
    (state) => state.user.user!.moduleUrls.oeeTrackerFrontendUrl,
  );

  private readonly endpointParameters = computed(() => {
    return uniq(
      flatten(
        this.dashboard()
          .widgets.map(({ typeBasedConfiguration }) =>
            'endpointParameters' in typeBasedConfiguration ? typeBasedConfiguration.endpointParameters : null,
          )
          .filter(Boolean) as number[][],
      ),
    );
  });

  constructor(
    private readonly translate: TranslateService,
    private readonly globalStore: Store<TReducerState>,
    private readonly pageHeaderService: PageHeaderService,
    private readonly snackbarService: SnackbarService,
    private readonly matDialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly injector: Injector,
    private readonly persistentEndpointParameterStore: PersistentEndpointParameterStore,
  ) {
    const { pageViewAndEditModeSwitchManager, countdownManager } = this.dialogData ?? this.route.snapshot.data;

    if (!countdownManager || !pageViewAndEditModeSwitchManager) {
      throw 'missing header elements set from router';
    }

    this.countdownManager = countdownManager;
    this.pageViewAndEditModeSwitchManager = pageViewAndEditModeSwitchManager;

    effect(() => {
      this.persistentEndpointParameterStore.loadEndpointParameters(this.endpointParameters());
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      ...this.mainOrSubDashboardExclusiveSubscriptions(),
      this.globalStore
        .select((state) => state.dashboardContent.dashboardData)
        .subscribe(() => {
          if (this.skipGridRebuildOnce) {
            this.skipGridRebuildOnce = false;

            return;
          }

          this.loadTheInitialGridState();
        }),
      this.pageViewAndEditModeSwitchManager.mode.pipe(distinctUntilChanged()).subscribe((mode) => {
        for (const gridItem of this.gridItems$.value) {
          gridItem.widgetInputs.isDummyMode.next(mode === 'edit');
        }
      }),
      this.countdownManager.timeoutSubject.subscribe((isTriggeredByUser) => {
        for (const gridItem of this.gridItems$.value) {
          if (isTriggeredByUser || gridItem.widgetData.configuration.refreshInterval === 0) {
            gridItem.widgetInputs.refreshSubject.next(null);
          }
        }
      }),
      this.gridItems$
        .pipe(
          debounceTime(100),
          switchMap((gridItems) => {
            return merge(...gridItems.map((item) => item.widgetInputs.dashboardRefreshSubject));
          }),
        )
        .subscribe(() => {
          this.countdownManager.timeoutSubject.next(true);
        }),
    );

    this.startScreenLock();
  }

  public ngAfterViewInit(): void {
    this.applyGridOverlayStyles();

    this.subscriptions.push(
      this.pageViewAndEditModeSwitchManager.mode.subscribe((mode) => {
        const isEditMode = mode === 'edit';

        if (mode === 'edit') {
          this.gridStackItems.forEach((item) => {
            item.elementRef?.nativeElement
              ?.querySelector('.grid-stack-item-content')
              ?.scrollTo({ behavior: 'smooth', top: 0 });
          });
        }

        // Updating this.gridOptions is not effective but doing the update just in case
        if (this.isMobileMode()) {
          this.gridOptions.disableDrag = true;
          this.gridOptions.disableDrag = true;
          this.gridstack.grid?.enableMove(false);
          this.gridstack.grid?.enableResize(false);
        } else {
          this.gridOptions.disableDrag = !isEditMode;
          this.gridOptions.disableResize = !isEditMode;
          this.gridstack.grid?.enableMove(isEditMode);
          this.gridstack.grid?.enableResize(isEditMode);
        }
      }),
    );
  }

  public onSubmitWidget(widget: IWidgetData): void {
    if (!this.selectedItem && typeof widget.widgetId !== 'number') {
      this.addWidget(widget);

      return;
    }

    const gridItem = this.gridItems$.value.find((item) => item.widgetData.widgetId === widget.widgetId);

    if (!gridItem) {
      return;
    }

    gridItem.widgetData = widget;
    gridItem.widgetInputs.configuration.next(widget.typeBasedConfiguration);

    const gridElement = this.getGridstackItem(widget.widgetId)!;
    this.gridstack.grid!.update(gridElement.el.gridstackNode!.el!, {
      h: widget.configuration.gridstackOptions.h,
      w: widget.configuration.gridstackOptions.w,
      x: widget.configuration.gridstackOptions.x,
      y: widget.configuration.gridstackOptions.y,
    });

    for (const gridstackItem of this.gridstack.gridstackItems!) {
      const item = this.gridItems$.value.find(
        ({ widgetData }) => widgetData.widgetId === get(gridstackItem.el.gridstackNode, 'widgetId'),
      );

      if (!item) {
        continue;
      }

      item.widgetData.configuration = {
        ...item.widgetData.configuration,
        gridstackOptions: {
          ...item.widgetData.configuration.gridstackOptions,
          x: gridstackItem.el.gridstackNode!.x,
          y: gridstackItem.el.gridstackNode!.y,
        },
      };

      item.gridstackOptions.x = gridstackItem.el.gridstackNode!.x;
      item.gridstackOptions.y = gridstackItem.el.gridstackNode!.y;
    }
  }

  public addWidget(widget: IWidgetData): void {
    const gridItem: TGridItem = this.convertWidgetToGridItem(widget);

    this.gridItems$.next([...this.gridItems$.value, gridItem]);
  }

  public onClickedWidgetEdit(selectedItem: TGridItem): void {
    this.selectedItem = selectedItem;
    const gridElement = this.getGridstackItem(selectedItem.widgetData.widgetId)!;
    selectedItem.widgetData.configuration.gridstackOptions = {
      h: gridElement.el.gridstackNode!.h!,
      w: gridElement.el.gridstackNode!.w!,
      x: gridElement.el.gridstackNode!.x!,
      y: gridElement.el.gridstackNode!.y!,
    };
    this.matDialog
      .open(WidgetAddEditDialogComponent, {
        closeOnNavigation: true,
        data: {
          editData: selectedItem.widgetData,
          type: selectedItem.widgetData.type,
          layout: this.dashboardLayout,
        } satisfies IAddEditDialogInjectData,
        disableClose: true,
        exitAnimationDuration: 0,
        maxWidth: '100%',
        restoreFocus: false,
        width: EDialogWidth.medium,
      })
      .afterClosed()
      .subscribe((widget: IWidgetData | undefined | null) => {
        if (!widget) {
          return;
        }

        this.onSubmitWidget(widget);
      });
  }

  public onNoteWidgetClicked(gridItem: TGridItem, isEditMode: boolean): void {
    const isQuickEditAllowed =
      !isEditMode &&
      gridItem.widgetData.type === EWidget.note &&
      typeof gridItem.widgetData.widgetId === 'number' &&
      !this.pageViewAndEditModeSwitchManager.isLockedInView();

    if (!isQuickEditAllowed) {
      return;
    }

    const currentNote =
      (gridItem.widgetData.typeBasedConfiguration as TWidgetCustomParameters[EWidget.note]).note ?? '';

    this.matDialog
      .open(EditNoteDialogComponent, {
        closeOnNavigation: true,
        data: { note: currentNote } as IEditNoteDialogData,
        disableClose: true,
        maxWidth: '100%',
        restoreFocus: false,
        width: EDialogWidth.medium,
      })
      .afterClosed()
      .subscribe((note: string | undefined) => {
        if (note === undefined || note === currentNote) {
          return;
        }

        this.applyNoteUpdate(gridItem, note);
      });
  }

  private applyNoteUpdate(gridItem: TGridItem, note: string): void {
    const { widgetId } = gridItem.widgetData;

    if (typeof widgetId !== 'number') {
      return;
    }

    const typeBasedConfiguration = {
      ...gridItem.widgetData.typeBasedConfiguration,
      note,
    } as TWidgetCustomParameters[EWidget.note];

    // Reflect the change immediately in the live widget, then persist it.
    gridItem.widgetData = { ...gridItem.widgetData, typeBasedConfiguration };
    gridItem.widgetInputs.configuration.next(typeBasedConfiguration);

    this.skipGridRebuildOnce = true;
    this.globalStore.dispatch(dashboardContentActions.updateNote({ note, widgetId }));
  }

  public onClickedWidgetRemove(selectedItem: TGridItem): void {
    this.selectedItem = selectedItem;
    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        cancelButtonText: this.translate.instant('button.cancel'),
        content: this.translate.instant('page.widgetContent.deleteDialog.content'),
        submitButtonText: this.translate.instant('button.remove'),
        title: this.translate.instant('page.widgetContent.deleteDialog.title'),
      },
    });

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.deleteWidget();
        }
      }),
    );
  }

  public showWidgetSubmissions(selectedItem: TGridItem): void {
    if (selectedItem.widgetData.type !== EWidget.formIo) {
      return;
    }

    const dialogData: IFormSubmissionsDialogData = {
      advancedFilters: selectedItem.widgetInputs.filterValuesVisibleToWidget(),
      closeButtonText: this.translate.instant('button.close'),
      dashboardId: this.dashboard().id,
      formId: (selectedItem.widgetData.typeBasedConfiguration as TWidgetCustomParameters[EWidget.formIo]).formId,
      title: this.translate.instant('page.formSubmissions.title'),
      widgetId: selectedItem.widgetData.widgetId as number,
    };

    this.matDialog.open(FormSubmissionsDialogComponent, {
      data: dialogData,
      disableClose: false,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.large,
    });
  }

  public clickHeaderAction(selectedItem: TGridItem, actionId: string): void {
    if (selectedItem.widgetInputs?.headerActions) {
      selectedItem.widgetInputs.headerActions.next(actionId);
    }
  }

  public deleteWidget(): void {
    const { selectedItem } = this;

    if (!selectedItem) {
      return;
    }

    const gridStackItem: GridstackItemComponent | undefined = this.getGridstackItem(selectedItem.widgetData.widgetId);

    if (!gridStackItem || !this.gridstack.grid) {
      return;
    }

    this.gridItems$.next(
      this.gridItems$.value.filter((item) => item.widgetData.widgetId !== selectedItem.widgetData.widgetId),
    );

    this.gridstack.grid.removeWidget(gridStackItem.el);
  }

  public switchToEditModeAndOpenAddModal(): void {
    this.pageViewAndEditModeSwitchManager.mode.next('edit');
    this.pageViewAndEditModeSwitchManager.addClicked.next();
  }

  public onClickedWidgetClone(widget: IWidgetData): void {
    this.matDialog
      .open(ConfirmationDialogComponent, {
        closeOnNavigation: true,
        data: {
          cancelButtonText: this.translate.instant('button.cancel'),
          content: this.translate.instant('page.widgetContent.cloneConfirmation.content'),
          submitButtonText: this.translate.instant('button.clone'),
          title: this.translate.instant('page.widgetContent.cloneConfirmation.title'),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }

        const clone = cloneDeep(widget);

        if (clone.configuration.gridstackOptions.x) {
          delete clone.configuration.gridstackOptions.x;
        }

        if (clone.configuration.gridstackOptions.y) {
          delete clone.configuration.gridstackOptions.y;
        }

        const gridElement = this.getGridstackItem(widget.widgetId)!;
        clone.configuration.gridstackOptions.w = gridElement.el.gridstackNode!.w!;
        clone.configuration.gridstackOptions.h = gridElement.el.gridstackNode!.h!;
        clone.widgetId = Symbol();

        this.addWidget(clone);
      });
  }

  public openSubDashboard(subDashboardId: number, dialogSize: TSubDashboardWindowSize = EDialogWidth.xl): void {
    const authorizedDashboards = this.authorizedDashboardIds();

    if (authorizedDashboards !== -1 && !authorizedDashboards.includes(subDashboardId)) {
      return;
    }

    this.dashboardContentService
      .getDashboardContent(subDashboardId, {
        params: this.dashboardContentService.dashboardContentLoadParams,
      })
      .pipe(take(1))
      .subscribe((response) => {
        if (!response?.data || !response.success) {
          return;
        }

        this.matDialog
          .open(DashboardContentComponent, {
            closeOnNavigation: true,
            data: {
              countdownManager: this.countdownManager,
              dashboard: response.data,
              pageViewAndEditModeSwitchManager: this.pageViewAndEditModeSwitchManager,
              subDashboardId,
            } satisfies IDashboardContentDialogData,
            disableClose: true,
            maxWidth: '100%',
            restoreFocus: false,
            width: dialogSize,
          })
          .afterClosed()
          .subscribe(() => {
            this.applyGridOverlayStyles();
          });
      });
  }

  private submit(): void {
    this.globalStore.dispatch(
      dashboardContentActions.reassignWidgets({
        dashboardId: this.dashboard().id,
        payload: (this.gridstack.gridstackItems ?? [])
          .map((gridStackItem: GridstackItemComponent) => {
            const gridItem = this.gridItems$.value.find(
              (item) => item.gridstackOptions.widgetId === get(gridStackItem, 'options.widgetId'),
            );

            if (!gridItem) {
              return null;
            }

            const gridstackOptions: IGridstackPositionSubmit = this.isMobileMode()
              ? gridItem.widgetData.configuration.gridstackOptions
              : {
                  h: gridStackItem.options.h!,
                  layout: this.dashboardLayout,
                  w: gridStackItem.options.w!,
                  x: gridStackItem.options.x!,
                  y: gridStackItem.options.y!,
                };

            return omit(
              {
                ...omit(
                  {
                    ...gridItem.widgetData,
                    configuration: {
                      ...gridItem.widgetData.configuration,
                      gridstackOptions,
                    },
                  },
                  'type',
                ),
                ...(typeof gridItem.gridstackOptions.widgetId === 'number'
                  ? { id: gridItem.gridstackOptions.widgetId }
                  : { type: gridItem.widgetData.type }),
              },
              'widgetId',
            );
          })
          .filter(Boolean) as TWidgetSavePayload[],
      }),
    );
  }

  private convertWidgetToGridItem<WidgetType extends EWidget = EWidget>(
    widget: IWidgetData<WidgetType>,
  ): TGridItem<WidgetType> {
    const lastAppliedFilters = runInInjectionContext(this.injector, () => {
      const initialValuesFromSave: IFilterValuesVisibleToWidget[] = widget.filters.map(
        ({ modeName, filters, suppressedFilters }) => ({
          filters: (filters.filter(({ field }) => !suppressedFilters.includes(field)) as TFilterSave[]).map(
            (values) => ({
              ...(pick(values, ['field', 'operator', 'value']) as TFilterSave),
              strict: true,
            }),
          ),
          modeName,
        }),
      );

      return signal<IFilterValuesVisibleToWidget[]>(cloneDeep(initialValuesFromSave));
    });
    const lastAppliedFilterSuppression = runInInjectionContext(this.injector, () => {
      const filtered = widget.filters.map(({ modeName, suppressedFilters }) => ({
        modeName,
        suppressedFilters,
      }));

      return signal<IWidgetFilterSuppression[]>(filtered);
    });
    const filterValuesVisibleToWidget = this.getFilterValuesVisibleToWidget(
      lastAppliedFilters,
      lastAppliedFilterSuppression,
    );
    const appliedFilters = runInInjectionContext(this.injector, () =>
      computed(() => {
        return this.getAppliedFilters(filterValuesVisibleToWidget(), widget);
      }),
    );

    const widgetInputs: TWidgetParameters<WidgetType> = {
      appliedFilters,
      configuration: new BehaviorSubject(widget.typeBasedConfiguration),
      dashboardId: this.dashboard().id,
      dashboardRefreshSubject: new Subject(),
      dataLoadStatusSubject: new BehaviorSubject<ELoadStatus>(ELoadStatus.initial),
      exceptionalCaseInformationSubject: new BehaviorSubject([] as IWidgetExceptionalCaseInformation[]),
      filterValuesVisibleToWidget,
      headerActions: new BehaviorSubject<string>(''),
      isDummyMode: new BehaviorSubject(this.pageViewAndEditModeSwitchManager.mode.value === 'edit'),
      lastDataLoadDateSubject: new BehaviorSubject<string | null>(null),
      refreshInterval: new BehaviorSubject<number>(widget.configuration.refreshInterval),
      refreshSubject: new BehaviorSubject<null>(null),
      widgetId: widget.widgetId,
    };

    const widgetLayout = widget.configuration.gridstackOptions?.layout || EDashboardLayout.fixedHeight;
    let { gridstackOptions } = widget.configuration;

    if (this.dashboardLayout && widgetLayout !== this.dashboardLayout) {
      gridstackOptions = addWidgetConfiguration[widget.type].defaultSize[this.dashboardLayout];
    }

    return {
      component: addWidgetConfiguration[widget.type].component,
      doesWidgetUseAnyFilter: runInInjectionContext(this.injector, () =>
        computed(() => {
          return this.filterCardService.doesWidgetUseAnyFilter<WidgetType>(widget.type, widget.typeBasedConfiguration);
        }),
      ),
      filtersForOpenDfPage: this.getFiltersForOpenDfPage(widget, appliedFilters),
      gridstackOptions: {
        ...gridstackOptions,
        widgetId: widget.widgetId,
      },
      headerActions: this.getProcessedHeaderActions(widgetConstants[widget.type].headerActions, widget),
      injector: Injector.create({
        providers: [
          {
            provide: WIDGET_INPUTS,
            useValue: widgetInputs,
          },
        ],
      }),
      isHighlighted: this.getHighlighted(widget, filterValuesVisibleToWidget, lastAppliedFilterSuppression),
      lastAppliedFilters,
      lastAppliedFilterSuppression,
      widgetData: widget,
      widgetInputs,
    };
  }

  private getHighlighted<WidgetType extends EWidget = EWidget>(
    widget: IWidgetData<WidgetType>,
    filterValuesVisibleToWidget: WritableSignal<IFilterValuesVisibleToWidget[] | undefined>,
    lastAppliedFilterSuppression: WritableSignal<IWidgetFilterSuppression[]>,
  ): Signal<boolean> {
    return runInInjectionContext(this.injector, () =>
      computed(() => {
        const hovered = this.advancedFilterService.manager.hoveredFilterIdentity();

        if (!hovered) {
          return false;
        }

        const canHoveredFilterApply = this.canHoveredFilterApply<WidgetType>(
          widget,
          filterValuesVisibleToWidget,
          lastAppliedFilterSuppression,
          hovered,
        );

        if (canHoveredFilterApply) {
          return true;
        }

        return this.isHoveredCustomFilterSelectedInWidget(widget, hovered);
      }),
    );
  }

  private canHoveredFilterApply<WidgetType extends EWidget = EWidget>(
    widget: IWidgetData<WidgetType>,
    filterValuesVisibleToWidget: WritableSignal<IFilterValuesVisibleToWidget[] | undefined>,
    lastAppliedFilterSuppression: WritableSignal<IWidgetFilterSuppression[]>,
    hovered: IAdvancedFilterIdentity,
  ): boolean {
    return widgetConstants[widget.type]
      .filterModes({
        typeBasedConfiguration: widget.typeBasedConfiguration,
      })
      .some(({ name, filters }) => {
        const visibleFiltersThatMatchMode = filterValuesVisibleToWidget()?.find(({ modeName }) => modeName === name);

        if (!visibleFiltersThatMatchMode) {
          return false;
        }

        const matchingSuppressedFilters =
          lastAppliedFilterSuppression().find(({ modeName }) => modeName === name)?.suppressedFilters ?? [];

        return filters.some(({ identity }) => {
          const isThereAStrictFilterAlreadyDefined = visibleFiltersThatMatchMode.filters.some(
            (filterValue) =>
              filterValue.strict &&
              identity.field === filterValue.field &&
              (identity.operators.includes(filterValue.operator) ||
                this.filterCardService.checkIfEqIsDemandedAndProvidedForSingleDropdown(
                  identity.operators,
                  filterValue,
                ) ||
                this.filterCardService.checkIfEqIsDemandedAndProvidedForDate(identity.operators, filterValue)),
          );

          return (
            !isThereAStrictFilterAlreadyDefined &&
            !matchingSuppressedFilters.includes(identity.field) &&
            identity.field === hovered.field &&
            (identity.operators.includes(hovered.operator) ||
              this.filterCardService.checkIfEqIsDemandedAndProvidedForSingleDropdown(identity.operators, {
                ...hovered,
                value: [1],
              } as TFilterSave) ||
              this.filterCardService.checkIfEqIsDemandedAndProvidedForDate(identity.operators, {
                ...hovered,
                value: { endDate: '2026-01-01', startDate: '2026-01-01' },
              } as TFilterSave))
          );
        });
      });
  }

  private isHoveredCustomFilterSelectedInWidget<WidgetType extends EWidget = EWidget>(
    widget: IWidgetData<WidgetType>,
    hovered: IAdvancedFilterIdentity,
  ): boolean {
    if (!('endpointParameters' in widget.typeBasedConfiguration)) {
      return false;
    }

    return (
      widget.typeBasedConfiguration.endpointParameters?.some((endpointParameter) => {
        const tags = this.persistentEndpointParameterStore.endpointParameters()[endpointParameter]?.tags;

        if (!tags?.length) {
          return false;
        }

        return tags.some((tag) => {
          if (tag.field === hovered.field) {
            return true;
          }

          if (!tag.field.startsWith('custom_')) {
            return false;
          }

          const idOfCustom = Number(tag.field.slice(7));

          return hovered.idOfCustom === idOfCustom;
        });
      }) ?? false
    );
  }

  private getFiltersForOpenDfPage<WidgetType extends EWidget = EWidget>(
    widget: IWidgetData<WidgetType>,
    appliedFilters: Signal<IAppliedFilters | undefined | null>,
  ): Signal<TInEffectFilter[]> {
    return runInInjectionContext(this.injector, () =>
      computed(() => {
        if (!widget.configuration.openDfPageConfiguration) {
          return [];
        }

        return openDfPageOptions[widget.configuration.openDfPageConfiguration].filters
          .map(({ field, operators }) => {
            const matchingValueFromAppliedFilters = appliedFilters()?.filters.find(
              (appliedFilter) =>
                appliedFilter.field === field && (operators as ESqlOperators[]).includes(appliedFilter.operator),
            );

            if (matchingValueFromAppliedFilters) {
              return matchingValueFromAppliedFilters;
            }

            return this.advancedFilterService.manager
              .appliedFilters()
              ?.find(
                (filterCardValue) =>
                  filterCardValue.field === field && (operators as ESqlOperators[]).includes(filterCardValue.operator),
              );
          })
          .filter(Boolean) as TInEffectFilter[];
      }),
    );
  }

  private getAppliedFilters<WidgetType extends EWidget = EWidget>(
    filterValuesVisibleToWidget: IFilterValuesVisibleToWidget[] | undefined,
    widget: IWidgetData<WidgetType>,
  ): IAppliedFilters | undefined | null {
    if (filterValuesVisibleToWidget === undefined) {
      return undefined;
    }

    const bestMode = this.filterCardService.extractBestFeasibleFilterCombination(widget.type, {
      currentFilterValues: filterValuesVisibleToWidget,
      typeBasedConfiguration: widget.typeBasedConfiguration,
    });

    return bestMode === null
      ? null
      : {
          filters: this.getFiltersThatMatchMode(
            bestMode.configurationOfBestMode.filters.map(({ identity }) => identity),
            filterValuesVisibleToWidget.find(({ modeName }) => modeName === bestMode.configurationOfBestMode.name)!
              .filters,
          ),
          modeName: bestMode.configurationOfBestMode.name,
        };
  }

  private getGridstackItem(widgetId: symbol | number): GridstackItemComponent | undefined {
    if (!widgetId) {
      return undefined;
    }

    return this.gridstack.gridstackItems?.find(
      (item: GridstackItemComponent) => get(item, 'options.widgetId') === widgetId,
    );
  }

  private loadTheInitialGridState(): void {
    this.gridItems$.next([]);
    this.gridstack?.grid?.removeAll();

    for (const data of this.dashboard().widgets) {
      this.addWidget({ ...cloneDeep(omit(data, 'id')), widgetId: data.id });
    }

    this.pageViewAndEditModeSwitchManager.mode.next('view');
  }

  public ngOnDestroy(): void {
    this.globalStore.dispatch(dashboardContentActions.resetReassignWidgetsStatus());

    if (this.ownsScreenLock()) {
      this.screenLockService.stop();
    }

    for (const subscription of this.subscriptions) {
      subscription?.unsubscribe();
    }
  }

  public openWidgetFilterDialog(gridItem: TGridItem): void {
    const filterValuesVisibleToWidget = gridItem.widgetInputs.filterValuesVisibleToWidget();

    if (!filterValuesVisibleToWidget) {
      return;
    }

    const dialogData: IWidgetFilterDialogData = {
      filterValuesVisibleToWidget,
      lastAppliedFilterSuppression: gridItem.lastAppliedFilterSuppression(),
      widgetType: gridItem.widgetData.type,
      widgetTypeBasedConfiguration: gridItem.widgetData.typeBasedConfiguration,
    };

    this.matDialog
      .open(WidgetFiltersDialogComponent, {
        autoFocus: false,
        data: dialogData,
        disableClose: true,
        maxWidth: '100%',
        restoreFocus: false,
        width: EDialogWidth.medium,
      })
      .afterClosed()
      .subscribe((dialogFilters?: IWidgetFilterDialogOutput) => {
        if (dialogFilters) {
          gridItem.lastAppliedFilters.set(dialogFilters.filters);
          gridItem.lastAppliedFilterSuppression.set(dialogFilters.filterSuppression);
        }
      });
  }

  /**
   * Only the board that *is* the screen drives the lock. Two hosts must keep their hands off it:
   *
   * - inside a slideshow iframe (`window.self !== window.top`) the parent slideshow owns the lock;
   * - inside a sub-dashboard dialog the board underneath owns it. This component runs in both, and calling
   *   `start()` from the dialog silently repointed the running lock at the sub-dashboard (`start()` sets the
   *   target before its idempotency check), while `stop()` on close switched the parent's lock off for good.
   *   Confirmed with the owner: the main board's configuration wins. See SCREEN_LOCK_CONTEXT.md.
   */
  private ownsScreenLock(): boolean {
    return window.self === window.top && !this.dialogData;
  }

  private startScreenLock(): void {
    if (!this.ownsScreenLock()) {
      return;
    }

    const dashboard = this.dashboard();
    const autoLock: IResolvedScreenLock | null | undefined = dashboard?.autoLock;

    if (!dashboard || !autoLock) {
      return;
    }

    this.screenLockService.start(
      { entityId: dashboard.id, entityName: dashboard.name, entityType: EScreenLockEntityType.dashboard },
      { idleTimeoutSeconds: autoLock.durationSeconds },
    );
  }

  private getFilterValuesVisibleToWidget(
    lastAppliedFilters: WritableSignal<IFilterValuesVisibleToWidget[]>,
    lastAppliedFilterSuppression: WritableSignal<IWidgetFilterSuppression[]>,
  ): WritableSignal<IFilterValuesVisibleToWidget[] | undefined> {
    return runInInjectionContext(this.injector, () =>
      linkedSignal<IFilterValuesVisibleToWidget[] | undefined>(() => {
        const filterCardValues = this.advancedFilterService.manager.appliedFilters();

        if (!filterCardValues) {
          return undefined; // Wait for Advanced filter prepare the default data
        }

        return lastAppliedFilters().map(({ modeName, filters }) => {
          return {
            filters: [
              ...filters.filter(
                ({ field }) =>
                  !lastAppliedFilterSuppression()
                    .find((suppression) => suppression.modeName === modeName)
                    ?.suppressedFilters.includes(field),
              ),
              ...filterCardValues
                .filter(
                  ({ field }) =>
                    !lastAppliedFilterSuppression()
                      .find((suppression) => suppression.modeName === modeName)
                      ?.suppressedFilters.includes(field),
                )
                .map((filterCardValue) => ({ ...filterCardValue, strict: false })),
            ],
            modeName,
          };
        });
      }),
    );
  }

  private getFiltersThatMatchMode(
    modeFilters: IFilterIdentity[],
    combinedValues: TInEffectFilter[],
  ): TInEffectFilter[] {
    return flatten(
      modeFilters.map((mode) => {
        const strictValues = this.getFiltersThatMatchModeBasedOnStrictness(mode, combinedValues, true);

        if (strictValues.length) {
          return strictValues;
        }

        return this.getFiltersThatMatchModeBasedOnStrictness(mode, combinedValues, false);
      }),
    );
  }

  private getFiltersThatMatchModeBasedOnStrictness(
    mode: IFilterIdentity,
    combinedValues: TInEffectFilter[],
    isNecessarilyStrict: boolean,
  ): TInEffectFilter[] {
    return combinedValues
      .filter(
        (value) =>
          mode.field === value.field &&
          this.filterCardService.checkIfProvidedOperatorMatchesAfterTransformation(mode.operators, value) &&
          (!isNecessarilyStrict || value.strict),
      )
      .map((matchingFilter): TInEffectFilter => {
        return this.filterCardService.getTransformedFilterValues(matchingFilter, mode.operators);
      });
  }

  public onClickedAddAction(widget: TGridItem): void {
    if (!widget.widgetData.configuration.actionTracker) {
      return;
    }

    const dialogData: IAddActionDialogConfig = {
      boardId: widget.widgetData.configuration.actionTracker?.boardId ?? null,
      cancelButtonText: this.translate.instant('button.cancel'),
      countdownManager: this.countdownManager,
      itemCategoryId: widget.widgetData.configuration.actionTracker?.itemCategoryId ?? null,
      itemTypeId: widget.widgetData.configuration.actionTracker?.itemTypeId ?? null,
    };

    const dialogRef = this.matDialog.open(AddActionDialogComponent, {
      data: dialogData,
      disableClose: true,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.large,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.countdownManager.timeoutSubject.next(true);
    });
  }

  private mainOrSubDashboardExclusiveSubscriptions(): Subscription[] {
    if (this.dialogData) {
      return [];
    }

    return [
      this.globalStore
        .select((state) => state.dashboardContent.dashboardData!.name)
        .subscribe((dashboardName) => {
          PageHeaderService.setPageHeader({ title: dashboardName });
          this.pageHeaderService.setTitle(dashboardName);
        }),
      this.globalStore
        .select((state) => state.dashboardContent.dashboardData!.parentId)
        .subscribe((parentId) => {
          const hasParent: boolean = parentId !== null && parentId !== undefined;
          PageHeaderService.setPageHeader({
            icon: hasParent ? 'account_tree' : 'dashboard',
            iconBackCommands: parentId ? DashboardsRoute.children(parentId) : null,
          });
        }),
      this.globalStore
        .select((state) => state.dashboardContent.dashboardData!.refreshInterval)
        .subscribe((refreshInterval) => {
          this.countdownManager.setCountdownSubject.next(refreshInterval ?? -1);
        }),
      this.pageViewAndEditModeSwitchManager.addClicked.subscribe(() => {
        this.selectedItem = undefined;
        this.matDialog
          .open(AddWidgetCatalogDialogComponent, {
            closeOnNavigation: true,
            data: { layout: this.dashboardLayout },
            disableClose: true,
            exitAnimationDuration: 0,
            maxWidth: '100%',
            restoreFocus: false,
            width: '90vw',
          })
          .afterClosed()
          .subscribe((widget: IWidgetData | undefined | null) => {
            if (!widget) {
              return;
            }

            this.onSubmitWidget(widget);
          });
      }),
      this.pageViewAndEditModeSwitchManager.cancelClicked.subscribe(() => {
        this.loadTheInitialGridState();
      }),
      this.pageViewAndEditModeSwitchManager.saveClicked.subscribe(() => {
        this.submit();
      }),
      this.globalStore
        .select((state) => state.dashboardContent.reassignWidgetsStatus)
        .pipe(filter((status) => status === ELoadStatus.success))
        .subscribe(() => {
          this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
          this.globalStore.dispatch(
            dashboardContentActions.fetchDashboardContents({ dashboardId: this.dashboard()!.id }),
          );
        }),
      combineLatest({
        gridItems: this.gridItems$.pipe(debounceTime(100)),
        pageMode: this.pageViewAndEditModeSwitchManager.mode,
      }).subscribe(({ pageMode, gridItems }) => {
        this.countdownManager.isHiddenAndSuspended.next(pageMode === 'edit' || !gridItems.length);
      }),
    ];
  }

  public openDfPage(widget: TGridItem): void {
    const configuration = openDfPageOptions[widget.widgetData.configuration.openDfPageConfiguration!];
    const inferredFiltersObs =
      configuration.inferredFilters?.(this.injector, widget.filtersForOpenDfPage()) ??
      new Observable<TFilterSave[]>().pipe(startWith([]));

    inferredFiltersObs.pipe(take(1)).subscribe((inferredFilters) => {
      const { url, originIncludes } = configuration.url(
        [...widget.filtersForOpenDfPage(), ...inferredFilters],
        this.oeeTrackerUrl(),
        runInInjectionContext(this.injector, () => inject(DateUtilitiesService)),
      );

      this.matDialog
        .open(IframeDialogComponent, {
          ...IframeDialogComponent.dialogParameters,
          data: {
            originIncludes,
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateStatus: (): void => {},
            url,
          } satisfies IframeDialogData,
          width: '90vw',
        })
        .afterClosed()
        .subscribe(() => {
          this.countdownManager.timeoutSubject.next(true);
        });
    });
  }

  public calculateFitToScreenVh(rowCount = 12, offsetPx = 120): string {
    const defaultValue = '8vh';

    if (typeof window === 'undefined') {
      return defaultValue;
    }

    try {
      const windowHeight = window.innerHeight;
      const availableHeightPx = windowHeight - offsetPx;
      const cellHeightPx = availableHeightPx / rowCount;
      const cellHeightVh = (cellHeightPx / windowHeight) * 100;

      return `${Number(cellHeightVh.toFixed(4))}vh`;
    } catch {
      return defaultValue;
    }
  }

  public getCellHeight(): string {
    let output;

    switch (this.dashboardLayout) {
      case EDashboardLayout.fitToScreen:
        output = this.calculateFitToScreenVh();
        break;
      case EDashboardLayout.fixedHeight:
        output = '10px';
        break;
    }

    return output;
  }

  public applyGridOverlayStyles(): void {
    const gridOverlayAr = document.querySelector('.grid-stack-area') as HTMLElement | null;

    if (!gridOverlayAr) {
      return;
    }

    const borderText: string = this.translate.instant('page.dashboards.layout.screenBottomBorder');

    gridOverlayAr.style.setProperty('--grid-columns', '12');
    gridOverlayAr.style.setProperty('--cell-height', this.getCellHeight());
    gridOverlayAr.style.setProperty('--page-break-label', `"${borderText}"`);
  }

  private getProcessedHeaderActions(actions: IHeaderAction[] | null, widget: IWidgetData): IHeaderAction[] {
    return (actions ?? []).map((action) => ({
      ...action,
      isVisible: this.checkActionVisibility(action.id, widget),
    }));
  }

  private checkActionVisibility(action: string, widget: IWidgetData): boolean {
    let isVisible = true;

    if (action === 'autofill') {
      const config = widget.typeBasedConfiguration as TWidgetCustomParameters[EWidget.formIo];
      isVisible = !!config?.formAutofillId;
    }

    return isVisible;
  }
}
